import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import { clamp, throttle } from "lodash-es";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { Title as TitleOriginal } from "./element/Title";
import tradeShowcaseImgSrc1 from "img/home/trade-showcase-1.png";
import tradeShowcaseImgSrc2 from "img/home/trade-showcase-2.png";
import tradeShowcaseImgSrc3 from "img/home/trade-showcase-3.png";
import tradeShowcaseImgSrc4 from "img/home/trade-showcase-4.png";
import tradeShowcaseMobileImgSrc4 from "img/home/trade-showcase-mobile-4.png";
import { MOBILE_WIDTH, TABLET_WIDTH, useIsTablet } from "hooks/useDeviceType";
import { useMediaQuery } from "usehooks-ts";

const MOBILE_WIDTH_CUSTOM = MOBILE_WIDTH + 100;
type WidthAndHeight = Pick<
  CSSProperties,
  "width" | "height" | "maxHeight" | "minHeight" | "minWidth" | "maxWidth"
>;

export const FeatureTradeShowcase = () => {
  const ref = useRef<HTMLDivElement>(null);
  const theme = GlobalStore.useState(s => s.theme);
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_WIDTH_CUSTOM}px)`);
  const isTablet = useIsTablet();

  const BACKGROUND_COLORS = [
    theme["Brand colours"].Green,
    theme["Brand colours"].Blue,
    theme["Brand colours"].Red,
    theme["Brand colours"]["Dark Blue"],
  ];
  const [sectionInFocus, setSectionInFocus] = useState(0);
  const content = useMemo(() => {
    const textAnimation = {
      initial: { y: 40, opacity: 0 },
      animate: { y: 0, opacity: 1 },
      exit: { y: -40, opacity: 0.5 },
      transition: { duration: 0.4, delay: 0 },
    };
    const imgAnimation = { ...textAnimation, transition: { delay: 0.2, duration: 0.4 } };

    switch (sectionInFocus) {
      case 0: {
        let widthAndHeight: WidthAndHeight = {
          maxHeight: "70%",
          height: "60%",
        };
        if (isTablet) {
          widthAndHeight = { width: "50%" };
        }
        if (isMobile) {
          widthAndHeight = { height: "60%", maxHeight: 360 };
          return (
            <>
              <ImgAnimated
                key={tradeShowcaseImgSrc1}
                src={tradeShowcaseImgSrc1}
                alt="Bebop token selector"
                style={widthAndHeight}
                {...imgAnimation}
              />
              <SubContainerTextContent key={sectionInFocus} {...textAnimation}>
                <Title>Every second counts.</Title>
                <Description>Trade quickly with our built-in actions and search</Description>
              </SubContainerTextContent>
            </>
          );
        }
        return (
          <>
            <SubContainerTextContent key={sectionInFocus} {...textAnimation}>
              <Title>Every second counts.</Title>
              <Description>Trade quickly with our built-in actions and search</Description>
            </SubContainerTextContent>
            <ImgAnimated
              key={tradeShowcaseImgSrc1}
              src={tradeShowcaseImgSrc1}
              alt="Bebop token selector"
              {...widthAndHeight}
              {...imgAnimation}
            />
          </>
        );
      }
      case 1: {
        let widthAndHeight: WidthAndHeight = {
          maxHeight: "80%",
          height: "80%",
        };
        if (isTablet) {
          widthAndHeight = { width: "54%" };
        }
        if (isMobile) {
          widthAndHeight = { height: "70%", maxHeight: 480 };
        }
        return (
          <>
            <ImgAnimated
              key={tradeShowcaseImgSrc2}
              src={tradeShowcaseImgSrc2}
              alt="Trading multiple tokens at once"
              style={widthAndHeight}
              {...imgAnimation}
            />
            <SubContainerTextContent key={sectionInFocus} {...textAnimation}>
              <Title>Many tokens, one trade, more savings</Title>
              <Description>Trade many tokens at once and save in gas fees</Description>
            </SubContainerTextContent>
          </>
        );
      }
      case 2: {
        let widthAndHeight: WidthAndHeight = {
          maxHeight: "60%",
          height: "60%",
        };
        if (isTablet) {
          widthAndHeight = { width: "52%" };
        }
        if (isMobile) {
          widthAndHeight = { height: "60%", maxHeight: 420 };
          return (
            <>
              <ImgAnimated
                key={tradeShowcaseImgSrc3}
                src={tradeShowcaseImgSrc3}
                alt="Approvals manager dialog"
                style={widthAndHeight}
                {...imgAnimation}
              />
              <SubContainerTextContent key={sectionInFocus} {...textAnimation}>
                <Title>Next-gen approvals</Title>
                <Description>
                  Approvals can be pricey - discover gas free permissions management without
                  compromising on security
                </Description>
              </SubContainerTextContent>
            </>
          );
        }
        return (
          <>
            <SubContainerTextContent key={sectionInFocus} {...textAnimation}>
              <Title>Next-gen approvals</Title>
              <Description>
                Approvals can be pricey - discover gas free permissions management without
                compromising on security
              </Description>
            </SubContainerTextContent>
            <ImgAnimated
              key={tradeShowcaseImgSrc3}
              src={tradeShowcaseImgSrc3}
              alt="Approvals manager dialog"
              style={widthAndHeight}
              {...imgAnimation}
            />
          </>
        );
      }
      case 3: {
        let widthAndHeight: WidthAndHeight = {
          height: "60%",
        };
        if (isTablet) {
          widthAndHeight = { width: "54%" };
        }
        if (isMobile) {
          widthAndHeight = { width: "100%", maxHeight: 330 };
        }
        return (
          <>
            <ImgAnimated
              key={tradeShowcaseImgSrc4}
              src={isMobile || isTablet ? tradeShowcaseMobileImgSrc4 : tradeShowcaseImgSrc4}
              alt="History page"
              {...widthAndHeight}
              {...imgAnimation}
            />
            <SubContainerTextContent key={sectionInFocus} {...textAnimation}>
              <Title>Track your trades</Title>
              <Description>Follow your history on Bebop</Description>
            </SubContainerTextContent>
          </>
        );
      }
      default:
        return;
    }
  }, [isMobile, isTablet, sectionInFocus]);

  const scrollPxPerSection = Math.floor(window.innerHeight / 2);
  const handleScroll = useCallback(() => {
    const top = ref.current?.getBoundingClientRect().top;
    if (top == null) return;
    const topCutoff = window.innerHeight / 5;
    if (top > topCutoff) return;
    const nextSectionInFocus = clamp(
      Math.floor((topCutoff - top) / scrollPxPerSection),
      0,
      BACKGROUND_COLORS.length - 1
    );
    throttle(() => setSectionInFocus(nextSectionInFocus), 300)();
  }, [BACKGROUND_COLORS.length, scrollPxPerSection]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <Container ref={ref}>
      <ScrollContainer>
        <SubContainer animate={{ background: BACKGROUND_COLORS[sectionInFocus] }}>
          <AnimatePresence>{content}</AnimatePresence>
        </SubContainer>
      </ScrollContainer>
    </Container>
  );
};

// Sub-components

const Container = styled(motion.div)`
  position: relative;
  margin: 10px 72px;
  z-index: 1;

  @media (max-width: ${MOBILE_WIDTH_CUSTOM}px) {
    margin: 27px 5%;
  }
`;

const ScrollContainer = styled.div`
  width: 100%;
  position: relative;
  height: 250dvh;
  text-align: center;
`;

const Description = styled.h4`
  color: ${props => props.theme.theme[1700]};
  text-align: center;
  font-family: Quincy CF;
  font-size: 30px;
  font-weight: 700;

  @media (max-width: ${TABLET_WIDTH}px) {
    font-size: 20px;
  }
  @media (max-width: ${MOBILE_WIDTH_CUSTOM}px) {
    font-size: 18px;
  }
`;

const SubContainer = styled(motion.div)`
  position: sticky;
  top: 10dvh;
  display: flex;
  padding: 20px 53px;
  gap: 56px;
  width: 100%;
  height: 35%;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 36px;

  @media (max-width: ${MOBILE_WIDTH_CUSTOM}px) {
    flex-direction: column;
    gap: 30px;
    top: 10dvh;
    height: 35%;
    padding: 20px 5%;
  }
`;

const SubContainerTextContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 50%;

  @media (max-width: ${MOBILE_WIDTH_CUSTOM}px) {
    width: 70%;
  }
`;

const Title = styled(TitleOriginal)`
  @media (max-width: ${TABLET_WIDTH}px) {
    font-size: 40px;
  }
  @media (max-width: ${MOBILE_WIDTH_CUSTOM}px) {
    font-size: 24px;
  }
`;

const ImgAnimated = styled(motion.img)``;
